import React from "react";

// Source
import StoreRole from "../store/Role";
import StoreRoute from "../store/Route";
import StoreLocalization from "../store/Localization";
import StorePreference from "../store/Preference";
import ControllerAlert from "./Alert";
import * as HelperSrc from "../HelperSrc";
import * as ApiUser from "../api/User";
import * as ModelUser from "../model/User";
import * as ModelRole from "../model/Role";
import * as ModelRoute from "../model/Route";
import * as ModelLocalization from "../model/Localization";
import * as ModelPreference from "../model/Preference";

export default class ControllerUserProfile {
    // Variable
    private storeRole: ModelRole.Istore;
    private storageRoleData = React.useRef<ModelRole.Idata | undefined>(undefined);
    private storeRoute: ModelRoute.Istore<ModelRoute.Idata>;
    private storageRouteData = React.useRef<ModelRoute.Idata | undefined>(undefined);
    private storeLocalization: ModelLocalization.Istore<ModelLocalization.Idata>;
    private storageLocalizationData = React.useRef<ModelLocalization.Idata | undefined>(undefined);
    private storePreference: ModelPreference.Istore<ModelPreference.Idata>;
    private storagePreferenceData = React.useRef<ModelPreference.Idata | undefined>(undefined);

    private controllerAlert: ControllerAlert;

    private elementViewMain = React.useRef<HTMLElement | null>(null);
    private elementBottom = React.useRef<HTMLElement | null>(null);

    private isRendered = React.useRef<boolean>(false);
    private validationForm = React.useRef<ModelUser.Idata | undefined>(undefined);
    private dataMain = React.useRef<ModelUser.Idata | undefined>(undefined);

    getApiLoadingList: Record<string, boolean>;
    setApiLoadingList: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

    getDataMain: ModelUser.Idata | undefined;
    setDataMain: React.Dispatch<React.SetStateAction<ModelUser.Idata | undefined>>;

    getPassword: string | undefined;
    setPassword: React.Dispatch<React.SetStateAction<string | undefined>>;

    getValidationForm: ModelUser.Idata | undefined;
    setValidationForm: React.Dispatch<React.SetStateAction<ModelUser.Idata | undefined>>;

    // Method
    constructor(isRendered: boolean) {
        this.storeRole = new StoreRole();
        this.storageRoleData.current = this.storeRole.storageGet();
        this.storeRoute = new StoreRoute();
        this.storageRouteData.current = this.storeRoute.storageGet();
        this.storeLocalization = new StoreLocalization();
        this.storageLocalizationData.current = this.storeLocalization.storageGet();
        this.storePreference = new StorePreference();
        this.storagePreferenceData.current = this.storePreference.storageGet();

        this.controllerAlert = new ControllerAlert(isRendered);

        this.isRendered.current = isRendered;

        const [getApiLoadingList, setApiLoadingList] = React.useState<Record<string, boolean>>({});
        this.getApiLoadingList = getApiLoadingList;
        this.setApiLoadingList = setApiLoadingList;

        const [getDataMain, setDataMain] = React.useState<ModelUser.Idata | undefined>(this.dataMain.current);
        this.getDataMain = getDataMain;
        this.setDataMain = setDataMain;

        const [getPassword, setPassword] = React.useState<string | undefined>(undefined);
        this.getPassword = getPassword;
        this.setPassword = setPassword;

        const [getValidationForm, setValidationForm] = React.useState<ModelUser.Idata | undefined>(this.validationForm.current);
        this.getValidationForm = getValidationForm;
        this.setValidationForm = setValidationForm;
    }

    initializeHook = (): void => {
        React.useEffect(() => {
            HelperSrc.domObserver(".view_user_profile", () => {
                this.initializeHtmlElement();
            });

            const historyState = window.history.state as Record<string, unknown> | undefined;

            if (historyState) {
                const parameterList = historyState.parameterList as ModelUser.Idata;

                if (parameterList) {
                    const checkboxRoleList = HelperSrc.convertRoleToCheckbox(this.storageRoleData.current, parameterList.role);

                    this.dataMain.current = { ...parameterList, ...checkboxRoleList };
                } else {
                    this.dataMain.current = {} as ModelUser.Idata;
                }
            } else {
                this.dataMain.current = {} as ModelUser.Idata;
            }

            this.setDataMain(this.dataMain.current);

            this.apiValidationForm();

            HelperSrc.storageEventListener("localization", () => {
                this.apiValidationForm();
            });
        }, []);

        React.useEffect(() => {
            this.storageRoleData.current = this.storeRole.storageGet();
            this.storageRouteData.current = this.storeRoute.storageGet();
            this.storageLocalizationData.current = this.storeLocalization.storageGet();
            this.storagePreferenceData.current = this.storePreference.storageGet();
        }, [this.storageRoleData.current, this.storageRouteData.current, this.storageLocalizationData.current, this.storagePreferenceData.current]);
    };

    clickChange = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        const element = event.target as HTMLElement;

        const checkForm = HelperSrc.checkForm(element, this.validationForm.current);

        if (!checkForm) {
            return;
        }

        this.setApiLoadingList((prev) => ({
            ...prev,
            profileChange: true
        }));

        if (this.dataMain.current && this.dataMain.current.id) {
            const id = this.dataMain.current.id;
            const username = this.readHtmlElementValue("username");
            const first_name = this.readHtmlElementValue("first_name");
            const last_name = this.readHtmlElementValue("last_name");
            const email = this.readHtmlElementValue("email");
            const is_active = this.readHtmlElementCheckbox("is_active");
            const is_checkbox_superuser = this.readHtmlElementCheckbox("is_checkbox_superuser");
            const is_checkbox_manager = this.readHtmlElementCheckbox("is_checkbox_manager");
            const is_checkbox_staff = this.readHtmlElementCheckbox("is_checkbox_staff");
            const is_checkbox_support = this.readHtmlElementCheckbox("is_checkbox_support");

            ApiUser.updateDataUserListProfile(
                id,
                username,
                this.readHtmlElementValue("password"),
                this.readHtmlElementValue("confirm_password"),
                first_name,
                last_name,
                email,
                is_active,
                is_checkbox_superuser,
                is_checkbox_manager,
                is_checkbox_staff,
                is_checkbox_support,
                (response) => {
                    this.setApiLoadingList((prev) => ({
                        ...prev,
                        profileChange: false
                    }));

                    if (response instanceof Error || response.message.error) {
                        if (response instanceof Error) {
                            HelperSrc.writeLog("error", response.message);
                        } else {
                            this.controllerAlert.show("error", response.message.error, false);
                        }

                        return;
                    }

                    this.controllerAlert.show("success", response.message.success, true);
                }
            );
        } else {
            ApiUser.createData(
                this.readHtmlElementValue("username"),
                this.readHtmlElementValue("password"),
                this.readHtmlElementValue("confirm_password"),
                this.readHtmlElementValue("first_name"),
                this.readHtmlElementValue("last_name"),
                this.readHtmlElementValue("email"),
                this.readHtmlElementCheckbox("is_active"),
                this.readHtmlElementCheckbox("is_checkbox_superuser"),
                this.readHtmlElementCheckbox("is_checkbox_manager"),
                this.readHtmlElementCheckbox("is_checkbox_staff"),
                this.readHtmlElementCheckbox("is_checkbox_support"),
                (response) => {
                    this.setApiLoadingList((prev) => ({
                        ...prev,
                        profileChange: false
                    }));

                    if (response instanceof Error || response.message.error) {
                        if (response instanceof Error) {
                            HelperSrc.writeLog("error", response.message);
                        } else {
                            this.controllerAlert.show("error", response.message.error, false);
                        }

                        return;
                    }

                    this.controllerAlert.show("success", response.message.success, true);
                }
            );
        }
    };

    private initializeHtmlElement = (): void => {
        if (!this.elementViewMain.current) {
            this.elementBottom.current = document.querySelector<HTMLElement>(".bottom");
            this.elementViewMain.current = document.querySelector<HTMLElement>(".view_user_profile");
        }
    };

    private readHtmlElementValue = (tag: string): string => {
        const element = document.querySelector<HTMLInputElement>(`[name='${tag}']`);

        return element ? element.value : "";
    };

    private readHtmlElementCheckbox = (tag: string): boolean => {
        const element = document.querySelector<HTMLInputElement>(`[name='${tag}']`);

        return element ? element.checked : false;
    };

    private apiValidationForm = (): void => {
        ApiUser.validationForm((response) => {
            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.validationForm.current = response.data.result as unknown as ModelUser.Idata;
            this.setValidationForm(this.validationForm.current);

            if (this.elementBottom.current) {
                HelperSrc.validationFormHelper(this.elementBottom.current, this.validationForm.current);
            }
        });
    };
}
